import { useEffect, useRef, useState } from "react";
import styles from "./icon-dropdown.module.css";

interface IconDropdownProps {
  trigger: React.ReactNode;
  items: {
    icon: string;
    text: string;
    onClick: () => void;
  }[];
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  align?: "right" | "center" | "left";
  positioningMode?: "css" | "absolute"; // CSS 기반 정렬 또는 절대값 ref 방식 선택
}

export default function IconDropdown({
  trigger,
  items,
  isOpen: controlledIsOpen,
  onToggle,
  align = "right",
  positioningMode = "absolute", // 기본값을 absolute로 설정
}: IconDropdownProps) {
  const [internalIsOpen, setIsInternalOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

  const setIsOpen = (open: boolean) => {
    if (controlledIsOpen !== undefined) {
      onToggle?.(open);
    } else {
      setIsInternalOpen(open);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("scroll", handleScroll, true); // capture phase로 이벤트 리스너 추가

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [setIsOpen]);

  const calculateDropdownPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const top = rect.bottom + window.scrollY + 8; // 8px 마진
      let left = rect.left + window.scrollX;

      // align에 따른 위치 조정
      if (align === "center") {
        left = rect.left + window.scrollX + rect.width / 2;
      } else if (align === "left") {
        left = rect.left + window.scrollX;
      } else {
        // right
        left = rect.right + window.scrollX;
      }

      setDropdownPosition({ top, left });
    }
  };

  const handleToggle = () => {
    if (!isOpen) {
      if (positioningMode === "absolute") {
        calculateDropdownPosition();
      }
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className={styles.dropdownWrapper} ref={dropdownRef}>
      <button type="button" onClick={handleToggle} ref={triggerRef}>
        {trigger}
      </button>

      {isOpen && (
        <div
          className={`${styles.dropdown} ${
            positioningMode === "css" ? styles[`align-${align}`] : ""
          }`}
          style={
            positioningMode === "absolute"
              ? {
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                }
              : undefined
          }
        >
          {items.map((item, index) => (
            <button
              key={index}
              onClick={() => {
                item.onClick();
                setIsOpen(false);
              }}
              className={styles.dropdownItem}
            >
              <div className={styles.dropdownIcon}>
                <img src={item.icon} width={1.25 * 16} height={1.25 * 16} alt={item.text} />
              </div>
              <span className={styles.dropdownText}>{item.text}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
